/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : HomologationPlanning.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-07-08
 *
 * Description    : Homologation Planning overview matrix by variant.
 * ============================================================================
 */

import React, { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { HandleApiActions, projectConfigAPI } from "../../../redux/actions";
import {
  selectProjectAction,
  selectVariantAction,
} from "../../../redux/actions/projectConfigActions";
import CustomDataGridV2 from "../common/datagrid/CustomDataGridV2";
import CustomDialogBoxV2 from "../common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../common/textField/CustomFormTextFieldV2";

const getIntersectionTabByTicketType = (ticketType) => {
  switch (Number(ticketType)) {
    case 1:
      return 3;
    case 2:
      return 2;
    case 3:
      return 1;
    case 6:
      return 0;
    default:
      return 0;
  }
};

const formatCreatedAt = (createdAt) => {
  if (!createdAt) return "-";
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return createdAt;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const buildHomologationGrid = (variantDetails = [], onActionClick, onEditClick) => {
  const variants = Array.isArray(variantDetails) ? variantDetails : [];
  const milestoneNames = [];
  const seenMilestones = new Set();

  variants.forEach((variant) => {
    (variant?.campaign_milestones || []).forEach((milestone) => {
      const milestoneName = milestone?.milestone_name;
      if (milestoneName && !seenMilestones.has(milestoneName)) {
        seenMilestones.add(milestoneName);
        milestoneNames.push(milestoneName);
      }
    });
  });

  const columns = [
    {
      field: "milestone_name",
      headerName: "Milestone",
      minWidth: 200,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        let isWholeVehicle = true;
        Object.keys(params.row).forEach((key) => {
          if (key.endsWith("_milestones")) {
            const milestones = params.row[key] || [];
            milestones.forEach((m) => {
              if (m.whole_vehicle === false) {
                isWholeVehicle = false;
              }
            });
          }
        });
        return (
          <Typography
            variant="body2"
            sx={{ opacity: isWholeVehicle ? 1 : 0.5 }}
          >
            {params.value}
          </Typography>
        );
      },
    },
    ...variants.map((variant) => {
      const field = `variant_${variant.variant_id}`;
      return {
        field,
        headerName: variant.variant_name || `Variant ${variant.variant_id}`,
        minWidth: 160,
        flex: 1,
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: (params) => {
          const milestones = params.row[`${field}_milestones`] || [];
          if (milestones.length === 0) return <Typography variant="body2">0</Typography>;

          return (
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
              {milestones.map((milestone, index) => (
                <Stack
                  key={`${milestone.vehicle_status_id ?? "none"}-${index}`}
                  direction="row"
                  alignItems="center"
                  spacing={0.25}
                >
                  <Typography
                    variant="body2"
                    sx={{ opacity: milestone.whole_vehicle === false ? 0.5 : 1 }}
                  >
                    {milestone.vehicle_count ?? 0}
                  </Typography>
                  
                  {milestone.whole_vehicle && (
                    <Tooltip title="Edit vehicle count">
                        <IconButton
                          size="small"
                          aria-label={`Edit vehicle count for ${milestone.milestone_name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onEditClick({ milestone, variant });
                          }}
                        >
                          <BorderColorIcon sx={{ color: "#000", fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                  )}
                  {milestone.action === true && milestone.whole_vehicle !== false && (
                    <Tooltip title="View action details">
                        <IconButton
                          size="small"
                          sx={{ color: "black" }}
                          aria-label={`View action details for ${milestone.milestone_name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            onActionClick({ milestone, variant });
                          }}
                        >
                          <ErrorOutlineIcon  sx={{ fontSize: "18px" }} />
                        </IconButton>
                      </Tooltip>
                  )}
                </Stack>
              ))}
            </Stack>
          );
        },
      };
    }),
  ];

  const rows = milestoneNames.map((milestoneName) => {
    const row = { id: milestoneName, milestone_name: milestoneName };

    variants.forEach((variant) => {
      const milestones = (variant?.campaign_milestones || []).filter(
        (item) => item?.milestone_name === milestoneName,
      );
      row[`variant_${variant.variant_id}`] = milestones.reduce(
        (total, milestone) => total + (Number(milestone?.vehicle_count) || 0),
        0,
      );
      row[`variant_${variant.variant_id}_milestones`] = milestones;
    });

    return row;
  });

  return { columns, rows };
};

function HomologationPlanning({ projectId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [variantDetails, setVariantDetails] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [actionDialog, setActionDialog] = useState({ open: false, context: null });
  const [actionDetails, setActionDetails] = useState([]);
  // const [actionError, setActionError] = useState("");
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, context: null });
  const [editedVehicleCount, setEditedVehicleCount] = useState(0);
  const [editedVehicleCountError, setEditedVehicleCountError] = useState("");
  const [isEditSaving, setIsEditSaving] = useState(false);
  const [actionPage, setActionPage] = useState(1);
  const [actionTotalItems, setActionTotalItems] = useState(0);
  const [overviewDialogOpen, setOverviewDialogOpen] = useState(false);
  const pageSize = 5;


  useEffect(() => {
    let isActive = true;

    const getHomologationDetails = async () => {
      if (!projectId) {
        setVariantDetails([]);
        return;
      }

      setIsLoading(true);
      setErrorMessage("");

      try {
        const response = await dispatch(
          HandleApiActions({
            ...projectConfigAPI.get_homologation_details,
            params: { project_id: projectId },
          }),
        );

        if (!isActive) return;

        if (response?.error) {
          // showErrorToast(response?.msg || "Unable to load homologation details.");
          setVariantDetails([]);
          setErrorMessage(response?.msg || "Unable to load homologation details.");
          return;
        }

        setVariantDetails(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        if (isActive) {
          // showErrorToast("Unable to load homologation details.");
          setVariantDetails([]);
          setErrorMessage("Unable to load homologation details.");
          console.error("Error fetching homologation details:", error);
        }
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    getHomologationDetails();
    return () => {
      isActive = false;
    };
  }, [dispatch, projectId]);

  const fetchActionDetails = async (context, page = 1) => {
    try {
      const response = await dispatch(
        HandleApiActions({
          ...projectConfigAPI.get_homologation_action_details,
          params: {
            project_id: projectId,
            variant_id: context.variant.variant_id,
            vehicle_status_id: context.milestone.vehicle_status_id,
            page_number: page,
            page_size: pageSize,
          },
        }),
      );

      if (response?.error) {
        // setActionError(response?.msg || "Unable to load action details.");
        return;
      }
      setActionDetails(Array.isArray(response?.data) ? response.data : []);
      setActionTotalItems(response?.total_count ?? response?.data?.length ?? 0);
    } catch (error) {
      // setActionError("Unable to load action details.");
      console.error("Error fetching homologation action details:", error);
    }
  };

  const handleActionClick = async (context) => {
    setActionDialog({ open: true, context });
    setActionDetails([]);
    // setActionError("");
    setActionPage(1);
    setActionTotalItems(0);
    setIsActionLoading(true);

    try {
      await fetchActionDetails(context, 1);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleActionPageChange = async (newPage) => {
    const context = actionDialog.context;
    if (!context) return;

    setActionPage(newPage);
    // setActionError("");
    setIsActionLoading(true);

    try {
      await fetchActionDetails(context, newPage);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleIntersectionClick = async (row) => {
    const intrId = row?.intr_id;
    if (!intrId) return;

    const tab = getIntersectionTabByTicketType(row?.ticket_type);
    const projectIdForRoute = row?.project_id ?? projectId;
    const variantIdForRoute =
      row?.variant_id ?? actionDialog.context?.variant?.variant_id;

    if (projectIdForRoute) {
      dispatch(selectProjectAction(Number(projectIdForRoute)));
    }

    if (projectIdForRoute) {
      await dispatch(
        HandleApiActions({
          ...projectConfigAPI.get_variants_main,
          params: {
            project_id: Number(projectIdForRoute),
            is_complete_details_required: true,
          },
          show_toast: false,
        }),
      );
    }

    if (variantIdForRoute) {
      dispatch(selectVariantAction(Number(variantIdForRoute)));
    }

    const queryParams = new URLSearchParams({
      tab: String(tab),
      intr_id: String(intrId),
    });

    if (projectIdForRoute) {
      queryParams.set("project_id", String(projectIdForRoute));
    }
    if (variantIdForRoute) {
      queryParams.set("variant_id", String(variantIdForRoute));
    }

    navigate(`/dashboard/v2/type-approval/intersection?${queryParams.toString()}`);
  };

  const handleEditClick = (context) => {
    setEditedVehicleCount(context.milestone.vehicle_count ?? 0);
    setEditedVehicleCountError("");
    setEditDialog({ open: true, context });
  };

  const handleEditSuccess = async () => {
    const context = editDialog.context;
    const vehicleCount = Number(editedVehicleCount);

    if (!Number.isInteger(vehicleCount) || vehicleCount < 0) {
      setEditedVehicleCountError("Enter a non-negative whole number.");
      return;
    }
    if (!context) return;

    setIsEditSaving(true);
    setEditedVehicleCountError("");
    try {
      const updateResponse = await dispatch(
        HandleApiActions({
          ...projectConfigAPI.update_homologation_vehicle_count,
          params: {
            vehicle_status_id: context.milestone.vehicle_status_id,
            project_id: projectId,
            variant_id: context.variant.variant_id,
            vehicle_count: vehicleCount,
          },
        }),
      );

      if (updateResponse?.error) {
        setEditedVehicleCountError(
          updateResponse?.msg || "Unable to update the vehicle count.",
        );
        return;
      }

      setIsLoading(true);
      const homologationResponse = await dispatch(
        HandleApiActions({
          ...projectConfigAPI.get_homologation_details,
          params: { project_id: projectId },
        }),
      );

      if (homologationResponse?.error) {
        // showErrorToast(
        //   homologationResponse?.msg || "Unable to refresh homologation details.",
        // );
        setErrorMessage(
          homologationResponse?.msg || "Unable to refresh homologation details.",
        );
      } else {
        setErrorMessage("");
        setVariantDetails(
          Array.isArray(homologationResponse?.data) ? homologationResponse.data : [],
        );
      }
      setEditDialog({ open: false, context: null });
    } catch (error) {
      setEditedVehicleCountError("Unable to update the vehicle count.");
      console.error("Error updating homologation vehicle count:", error);
    } finally {
      setIsEditSaving(false);
      setIsLoading(false);
    }
  };

  const { columns, rows } = useMemo(
    () => buildHomologationGrid(variantDetails, handleActionClick, handleEditClick),
    // The callback uses the current project ID and dispatch instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [variantDetails, projectId, dispatch],
  );

  const actionRows = useMemo(
    () =>
      actionDetails.map((item, index) => ({
        ...item,
        id: `${item.intr_id || "intersection"}-${item.run_id || "no-run"}-${index}`,
        ticket_type: item.ticket_type ?? item.tickets_type ?? "-",
        serial_number: index + 1,
        created_at_formatted: formatCreatedAt(item.created_at),
        is_deleted_count: item?.is_deleted_count
      })),
    [actionDetails],
  );

  const handleActionConfirm = async (intrId, isDeletedCount, runId) => {
    const context = actionDialog.context;
    if (!context) return;

    const params = {
      project_id: projectId,
      variant_id: context.variant.variant_id,
      vehicle_status_id: context.milestone.vehicle_status_id,
      intr_id: intrId,
      is_deleted_count: isDeletedCount,
      ...(runId !== null && runId !== undefined && { run_id: runId }),
    };

    // setActionError("");
    try {
      const confirmResponse = await dispatch(
        HandleApiActions({
          ...projectConfigAPI.homologation_action_confirm,
          params,
          show_toast: true
        }),
      );

      if (confirmResponse?.error) {
        // setActionError(confirmResponse?.msg || "Unable to confirm the action.");
        return;
      }

      setIsLoading(true);
      setIsActionLoading(true);
      const [homologationResponse, actionResponse] = await Promise.all([
        dispatch(
          HandleApiActions({
            ...projectConfigAPI.get_homologation_details,
            params: { project_id: projectId },
          }),
        ),
        dispatch(
          HandleApiActions({
            ...projectConfigAPI.get_homologation_action_details,
            params: {
              project_id: projectId,
              variant_id: context.variant.variant_id,
              vehicle_status_id: context.milestone.vehicle_status_id,
              page_number: actionPage,
              page_size: pageSize,
            },
          }),
        ),
      ]);

      if (homologationResponse?.error) {
        // showErrorToast(
        //   homologationResponse?.msg || "Unable to refresh homologation details.",
        // );
        setErrorMessage(
          homologationResponse?.msg || "Unable to refresh homologation details.",
        );
      } else {
        setErrorMessage("");
        setVariantDetails(
          Array.isArray(homologationResponse?.data) ? homologationResponse.data : [],
        );
      }

      if (actionResponse?.error) {
        // setActionError(actionResponse?.msg || "Unable to refresh action details.");
      } else {
        setActionDetails(Array.isArray(actionResponse?.data) ? actionResponse.data : []);
        setActionTotalItems(actionResponse?.total_count ?? actionResponse?.data?.length ?? 0);
      }
    } catch (error) {
      // setActionError("Unable to confirm the action.");
      console.error("Error confirming homologation action:", error);
    } finally {
      setIsLoading(false);
      setIsActionLoading(false);
    }
  };

  const actionColumns = [
    { field: "serial_number", headerName: "S.No", width: 80, sortable: false },
    {
      field: "details",
      headerName: "Details",
      minWidth: 400,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Box>
          
          <Typography variant="body2" sx={{ fontWeight: 700, color: "#53565A" }}>
            Intersection ID:{" "}
            <Typography
              component="button"
              type="button"
              variant="body2"
              onClick={() => handleIntersectionClick(row)}
              sx={{
                p: 0,
                border: 0,
                color: "#53565A",
                cursor: row.intr_id ? "pointer" : "default",
                font: "inherit",
                fontWeight: 700,
                textDecoration: row.intr_id ? "underline" : "none",
                backgroundColor: "transparent",
              }}
            >
              {row.intr_id || "-"}
            </Typography>
          </Typography>
          {(row.test_case || []).map((testCase, index) => (
            <React.Fragment key={`${testCase?.test_case_name || "test-case"}-${index}`}>
              <Typography variant="body2">
                Test case: {testCase?.test_case_name || "-"}
              </Typography>
              <Typography variant="body2">
                Count: {testCase?.vehicle_count ?? 0}
              </Typography>
              {
                row?.run_id && 
                   <Typography variant="body2">
                Run ID: {row?.run_id }
              </Typography>
              }
            </React.Fragment>
          ))}
        </Box>
      ),
    },
    {
      field: "created_at_formatted",
      headerName: "Created At",
      minWidth: 140,
      sortable: false,
    },
    {
      field: "action",
      headerName: "Action",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title="Confirm action">
          <IconButton
            color="success"
            aria-label={`Confirm intersection ${row.intr_id}`}
            onClick={() =>
              handleActionConfirm(row.intr_id, row.is_deleted_count, row.run_id)
            }
          >
            {row.is_deleted_count ? <RemoveCircleIcon /> : <AddCircleIcon />}
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const statusMessage = isLoading
    ? "Loading homologation details..."
    : rows.length === 0 ? "No homologation details available." : "";

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontFamily: "Manrope, sans-serif",
            textAlign: "left",
          }}
        >
          Overview
        </Typography>
        {!statusMessage && (
          <Tooltip title="View overview">
            <IconButton
              size="small"
              aria-label="View overview"
              onClick={() => setOverviewDialogOpen(true)}
              sx={{ color: "#000" }}
            >
              <VisibilityIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      
        <Box sx={{ height: Math.max(180, rows.length * 52 + 58), width: "100%" }}>
          <CustomDataGridV2 rows={rows} columns={columns} />
        </Box>
   

      <CustomDialogBoxV2
        open={overviewDialogOpen}
        onClose={() => setOverviewDialogOpen(false)}
        title="Overview"
        hideDefaultActions
        maxWidth="lg"
        paperSx={{ width: "1100px", maxWidth: "95vw" }}
      >
        <Box
          sx={{
            height: Math.min(650, Math.max(260, rows.length * 52 + 80)),
            width: "100%",
          }}
        >
          <CustomDataGridV2 rows={rows} columns={columns} />
        </Box>
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={actionDialog.open}
        onClose={() => setActionDialog({ open: false, context: null })}
        title={`Action Details - ${actionDialog.context?.milestone?.milestone_name || ""}`}
        hideDefaultActions
        maxWidth="md"
        paperSx={{ width: "900px", maxWidth: "95vw" }}
      >
        {isActionLoading ? (
          <Typography color="text.secondary">Loading action details...</Typography>
        ) :  actionRows.length === 0 ? (
          <Typography color="text.secondary">No action details available.</Typography>
        ) : (
          <Box
            sx={{
              height: Math.min(520, Math.max(220, actionRows.length * 82 + 112)),
              width: "100%",
            }}
          >
            <CustomDataGridV2
              rows={actionRows}
              columns={actionColumns}
              page={actionPage}
              rowsPerPage={pageSize}
              handlePageChange={handleActionPageChange}
              totalItems={actionTotalItems}
              showPagination={true}
              isMultiLine
            />
          </Box>
        )}
      </CustomDialogBoxV2>

      <CustomDialogBoxV2
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, context: null })}
        title="Edit Vehicle Count"
        successButtonName="Save"
        rejectButtonName="Cancel"
        actionButtonStyle="continuous"
        onClickSuccess={handleEditSuccess}
        onClickReject={() => setEditDialog({ open: false, context: null })}
        onDisabled={isEditSaving}
        paperSx={{ width: "420px" }}
      >
        <CustomFormTextFieldV2
          label="Vehicle Count"
          type="number"
          fldType="number"
          required
          value={editedVehicleCount}
          setValue={setEditedVehicleCount}
          valueError={editedVehicleCountError}
          setValueError={setEditedVehicleCountError}
          inputProps={{ inputProps: { min: 0 } }}
          disabled={isEditSaving}
        />
      </CustomDialogBoxV2>
    </Box>
  );
}

export default HomologationPlanning;
